import * as XLSX from 'xlsx';
import JSZip from 'jszip';

export type ExcelMetadataRow = {
  label: string;
  value: string;
};

export type ParsedExcelSheet = {
  name: string;
  companyInfo: string[];
  website: string;
  metadata: ExcelMetadataRow[];
  notes: ExcelMetadataRow[];
  headers: string[];
  rows: string[][];
  excelRowNumbers: number[];
};

export type ParsedExcelWorkbook = {
  logos: string[];
  sheets: ParsedExcelSheet[];
};

const HEADER_MARKER_SETS: RegExp[][] = [
  [/animal\s*code/i, /item\s*type/i],
  [/deposit\s*id/i, /site\s*code/i],
  [/deposit\s*id/i, /straw\s*number/i],
];
const METADATA_LABELS = new Set(['template', 'generated at', 'generated by']);
const FALLBACK_LOGO = '/images/rsg-horizontal-full-color.png';

export const parseExcelFile = async (file: File): Promise<ParsedExcelWorkbook> => {
  const buffer = await file.arrayBuffer();
  const [logos, workbook] = await Promise.all([extractExcelLogos(buffer), Promise.resolve(XLSX.read(buffer, { type: 'array' }))]);
  const resolvedLogos = logos.length ? logos : [FALLBACK_LOGO];

  if (!workbook.SheetNames.length) {
    return { logos: resolvedLogos, sheets: [] };
  }

  const sheets = workbook.SheetNames.map(sheetName => parseSheet(workbook.Sheets[sheetName], sheetName)).filter(
    sheet => sheet.headers.length > 0 || sheet.rows.length > 0,
  );

  return {
    logos: resolvedLogos,
    sheets,
  };
};

export const revokeParsedExcelLogos = (workbook: ParsedExcelWorkbook | null) => {
  workbook?.logos.forEach(logo => {
    if (logo.startsWith('blob:')) {
      URL.revokeObjectURL(logo);
    }
  });
};

const parseSheet = (sheet: XLSX.WorkSheet, name: string): ParsedExcelSheet => {
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    blankrows: true,
    raw: false,
  });

  const normalizedRows = rawRows.map(row => normalizeRow(row as unknown[]));
  const headerRowIndex = findHeaderRowIndex(normalizedRows);

  if (headerRowIndex < 0) {
    return parseAsSimpleSheet(normalizedRows, name);
  }

  const preambleRows = normalizedRows.slice(0, headerRowIndex);
  const headers = trimTrailingEmpty(normalizedRows[headerRowIndex]);
  const columnCount = headers.length;
  const dataRows: string[][] = [];
  const excelRowNumbers: number[] = [];

  normalizedRows.slice(headerRowIndex + 1).forEach((row, offset) => {
    const paddedRow = padRow(row, columnCount);
    if (!paddedRow.some(cell => cell !== '')) {
      return;
    }

    dataRows.push(paddedRow);
    excelRowNumbers.push(headerRowIndex + 2 + offset);
  });

  return {
    name,
    companyInfo: extractCompanyInfo(preambleRows),
    website: extractWebsite(preambleRows),
    metadata: extractMetadata(preambleRows),
    notes: extractNotes(preambleRows),
    headers,
    rows: dataRows,
    excelRowNumbers,
  };
};

const parseAsSimpleSheet = (normalizedRows: string[][], name: string): ParsedExcelSheet => {
  const nonEmptyRows = normalizedRows
    .map((row, index) => ({ row, excelRowNumber: index + 1 }))
    .filter(item => item.row.some(cell => cell !== ''));
  if (!nonEmptyRows.length) {
    return {
      name,
      companyInfo: [],
      website: '',
      metadata: [],
      notes: [],
      headers: [],
      rows: [],
      excelRowNumbers: [],
    };
  }

  const headers = trimTrailingEmpty(nonEmptyRows[0].row);
  const rows: string[][] = [];
  const excelRowNumbers: number[] = [];

  nonEmptyRows.slice(1).forEach(item => {
    const paddedRow = padRow(item.row, headers.length);
    if (!paddedRow.some(cell => cell !== '')) {
      return;
    }

    rows.push(paddedRow);
    excelRowNumbers.push(item.excelRowNumber);
  });

  return {
    name,
    companyInfo: [],
    website: '',
    metadata: [],
    notes: [],
    headers,
    rows,
    excelRowNumbers,
  };
};

const extractExcelLogos = async (buffer: ArrayBuffer): Promise<string[]> => {
  try {
    const zip = await JSZip.loadAsync(buffer);
    const mediaFolder = zip.folder('xl/media');
    if (!mediaFolder) {
      return [];
    }

    const imageEntries = Object.values(zip.files)
      .filter(entry => entry.name.startsWith('xl/media/') && !entry.dir)
      .sort((a, b) => a.name.localeCompare(b.name));

    const firstImage = imageEntries[0];
    if (!firstImage) {
      return [];
    }

    const blob = await firstImage.async('blob');
    const extension = firstImage.name.split('.').pop()?.toLowerCase();
    const mimeType = extension === 'png' ? 'image/png' : extension === 'jpg' || extension === 'jpeg' ? 'image/jpeg' : blob.type;
    return [URL.createObjectURL(new Blob([blob], { type: mimeType || 'image/png' }))];
  } catch {
    return [];
  }
};

const normalizeRow = (row: unknown[]): string[] => row.map(formatExcelCell);

const findHeaderRowIndex = (rows: string[][]): number =>
  rows.findIndex(row => HEADER_MARKER_SETS.some(markers => markers.every(marker => row.some(cell => marker.test(cell)))));

const extractCompanyInfo = (preambleRows: string[][]): string[] => {
  const companyLines: string[] = [];

  for (const row of preambleRows.slice(0, 6)) {
    const rightSideCells = row
      .slice(Math.max(0, row.length - 4))
      .map(cell => cell.trim())
      .filter(Boolean)
      .filter(cell => !isWebsite(cell) && !isMetadataLabel(cell) && !isNoteLabel(cell));

    rightSideCells.forEach(cell => {
      if (!companyLines.includes(cell)) {
        companyLines.push(cell);
      }
    });
  }

  return companyLines;
};

const extractWebsite = (preambleRows: string[][]): string => {
  for (const row of preambleRows) {
    const website = row.find(isWebsite);
    if (website) {
      return website;
    }
  }
  return '';
};

const extractMetadata = (preambleRows: string[][]): ExcelMetadataRow[] => {
  const metadata: ExcelMetadataRow[] = [];

  for (const row of preambleRows) {
    const labelIndex = row.findIndex(cell => METADATA_LABELS.has(cell.toLowerCase().replace(/:$/, '')));
    if (labelIndex >= 0) {
      const rawLabel = row[labelIndex].replace(/:$/, '');
      const value = row.slice(labelIndex + 1).find(cell => cell.trim() !== '');
      metadata.push({ label: rawLabel, value: value ?? '' });
      continue;
    }

    const combined = row.find(cell => /^(template|generated at|generated by)\s*:/i.test(cell));
    if (combined) {
      const [label, ...rest] = combined.split(':');
      metadata.push({ label: label.trim(), value: rest.join(':').trim() });
    }
  }

  return metadata;
};

const extractNotes = (preambleRows: string[][]): ExcelMetadataRow[] => {
  const notes: ExcelMetadataRow[] = [];

  for (const row of preambleRows) {
    const noteLabelIndex = row.findIndex(cell => /^note(\s*\d+)?\b/i.test(cell.trim()));
    if (noteLabelIndex < 0) {
      continue;
    }

    const labelCell = row[noteLabelIndex].trim();
    const label = labelCell.replace(/:$/, '');

    if (/^note(\s*\d+)?\s*:/i.test(labelCell) && labelCell.includes(':') && labelCell.length > label.split(':')[0].length + 1) {
      notes.push({ label: label.split(':')[0].trim(), value: labelCell.replace(/^note(\s*\d+)?\s*:\s*/i, '').trim() });
      continue;
    }

    const value = row
      .slice(noteLabelIndex + 1)
      .filter(Boolean)
      .join(' ')
      .trim();

    if (value) {
      notes.push({ label, value });
    }
  }

  return notes;
};

const isWebsite = (value: string) => /reprospecialty\.com/i.test(value) || /^https?:\/\//i.test(value);

const isMetadataLabel = (value: string) => METADATA_LABELS.has(value.toLowerCase().replace(/:$/, ''));

const isNoteLabel = (value: string) => /^note(\s*\d+)?\b/i.test(value.trim());

const trimTrailingEmpty = (row: string[]): string[] => {
  let end = row.length;
  while (end > 0 && row[end - 1] === '') {
    end -= 1;
  }
  return row.slice(0, end);
};

const padRow = (row: string[], columnCount: number): string[] =>
  Array.from({ length: columnCount }, (_, index) => row[index] ?? '');

const formatExcelCell = (cell: unknown): string => {
  if (cell == null) {
    return '';
  }
  if (typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean') {
    return String(cell).trim();
  }
  if (cell instanceof Date) {
    return cell.toISOString();
  }
  return '';
};
